import React from 'react';
import { Box, Typography } from '@mui/material';
import { ArrowDropDown, ArrowDropUp, Remove } from '@mui/icons-material';
import { Delta, Direction } from '../utils/metrics';

/** Delta colours are semantic (good / bad / neutral) and separate from the chart accent. */
const COLORS: Record<Direction, string> = {
  up: '#047857',
  down: '#B91C1C',
  flat: '#4B5563',
  na: '#9CA3AF',
};

const ICONS = { up: ArrowDropUp, down: ArrowDropDown, flat: Remove } as const;

export interface DeltaTextProps {
  delta: Delta | null;
  /** Plain text after the value, e.g. "(−8.5%) vs prior 6 months". */
  suffix?: string;
  size?: 'small' | 'medium';
}

/**
 * A change against the prior period: an arrow (or dash) plus the value, never
 * colour alone. Renders "—" when there is nothing to compare.
 * @example <DeltaText delta={{ direction: 'down', text: '8.8' }} suffix="(−8.5%) vs prior 6 months" />
 */
export const DeltaText: React.FC<DeltaTextProps> = ({ delta, suffix, size = 'small' }) => {
  const fontSize = size === 'small' ? '0.8rem' : '0.95rem';

  if (!delta || delta.direction === 'na') {
    return (
      <Typography component="span" sx={{ color: COLORS.na, fontSize, fontWeight: 500 }}>
        -
      </Typography>
    );
  }

  const Icon = ICONS[delta.direction];
  return (
    <Box
      component="span"
      aria-label={`${delta.direction} ${delta.text}`}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        color: COLORS[delta.direction],
        fontWeight: 600,
        fontSize,
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
      }}
    >
      <Icon aria-hidden="true" sx={{ fontSize: size === 'small' ? 18 : 22, ml: -0.5, mr: -0.25 }} />
      <span>{delta.text}</span>
      {suffix ? (
        // A non-breaking space: a plain space at the start of this inline box is
        // collapsed by the browser, which glued the value to the suffix.
        <Typography component="span" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: 'inherit', whiteSpace: 'normal' }}>
          {' '}
          {suffix}
        </Typography>
      ) : null}
    </Box>
  );
};
